import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Clock, Building2, UserPlus, FileSignature, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-4xl font-bold text-[#333333] mb-2">DASHBOARD</h1>
      <p className="text-[#52525B] mb-8">Welcome to Aggreko Sweden AB Management System</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8 stagger-children">
        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">Total Team Members</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.total_workers || 0}</p>
              </div>
              <Users className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">Agreements</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.total_agreements || 0}</p>
              </div>
              <FileSignature className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">Timesheets</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.total_timesheets || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">Manpower Workers</p>
                <p className="text-3xl font-bold text-[#333333] mt-1">{stats?.manpower_workers || 0}</p>
              </div>
              <Building2 className="w-10 h-10 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card cursor-pointer hover:shadow-lg" onClick={() => navigate('/admin/requests')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold uppercase text-[#52525B]">Pending Requests</p>
                <p className="text-3xl font-bold text-[#F37021] mt-1">{stats?.pending_requests || 0}</p>
              </div>
              <UserPlus className="w-10 h-10 text-[#F37021]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Timesheets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">RECENT TIMESHEETS</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_timesheets?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Team Member</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_timesheets.map((ts) => (
                  <TableRow key={ts.id}>
                    <TableCell>{ts.date}</TableCell>
                    <TableCell>{ts.worker_name}</TableCell>
                    <TableCell>{ts.project_name}</TableCell>
                    <TableCell>{ts.start_time} - {ts.end_time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[#A1A1AA] text-center py-8">No recent timesheets</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
